import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Fade,
  Slide,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { loginWithGoogle } from '../services/authService';
import { GoogleLogin } from '@react-oauth/google';
import { GOOGLE_WEB_CLIENT_ID } from '../config/api';
import logo from '../assets/logo_512.png';

const accent = '#c45c5c';
const bgGradient = 'linear-gradient(165deg, #fdf8f6 0%, #f5ebe6 50%, #ede4df 100%)';

const Login = () => {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const { login, user, loading: authLoading } = useAuth();
  const googleReady = Boolean(GOOGLE_WEB_CLIENT_ID);

  useEffect(() => {
    if (!authLoading && user && user.userType === 'customer') {
      navigate('/', { replace: true });
    }
  }, [user, authLoading, navigate]);

  if (authLoading) {
    return (
      <Container maxWidth="sm" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  const handleGoogleSuccess = async (credentialResponse) => {
    const cred = credentialResponse?.credential;
    if (!cred) {
      setError('Could not read Google sign-in credential');
      return;
    }
    setGoogleLoading(true);
    setError('');
    setSuccess('');
    try {
      const result = await loginWithGoogle(cred, 'customer');
      if (result.success) {
        if (result.isNewUser) {
          setSuccess('Account created successfully! Redirecting...');
        } else {
          setSuccess('Welcome back! Redirecting...');
        }
        login(result.user, result.token);
        setTimeout(() => navigate('/', { replace: true }), 800);
      }
    } catch (err) {
      setError(err.message || 'Google sign-in failed');
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: bgGradient,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 3, sm: 4 },
        px: 2,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -100,
          right: -100,
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(196, 92, 92, 0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: -150,
          left: -150,
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(196, 92, 92, 0.06) 0%, transparent 70%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Fade in timeout={600}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4 },
              width: '100%',
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'rgba(0,0,0,0.06)',
              bgcolor: 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(12px)',
              boxShadow: '0 24px 48px rgba(0,0,0,0.06)',
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box
                component="img"
                src={logo}
                alt="MarryTube"
                sx={{
                  height: { xs: 160, sm: 200 },
                  width: 'auto',
                  maxWidth: '100%',
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.08))',
                }}
              />
              <Typography variant="h6" sx={{ mt: 2, fontWeight: 700, color: '#2d2d2d' }}>
                Welcome
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {googleReady
                  ? 'Sign in only with your Google account'
                  : 'Google Sign-In is required'}
              </Typography>
            </Box>

            {googleReady ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                {/* useOneTap off: avoids FedCM / 403 prompts; fix "origin not allowed" in Google Cloud Console */}
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() =>
                    setError(
                      'Google sign-in blocked. In Google Cloud → OAuth Client, add Authorized JavaScript origin for this URL (exact match including http/https and localhost vs 127.0.0.1).',
                    )}
                  useOneTap={false}
                  theme="outline"
                  size="large"
                  text="continue_with"
                  shape="pill"
                  width={320}
                />
                {googleLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <CircularProgress size={28} sx={{ color: accent }} />
                  </Box>
                )}
              </Box>
            ) : (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                Set <strong>REACT_APP_GOOGLE_CLIENT_ID</strong> in your <code>.env</code> file (same Web Client ID as
                backend <strong>GOOGLE_CLIENT_ID</strong>), then restart the app.
              </Alert>
            )}

            {error && (
              <Slide direction="down" in={Boolean(error)}>
                <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              </Slide>
            )}

            {success && (
              <Slide direction="down" in={Boolean(success)}>
                <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
                  {success}
                </Alert>
              </Slide>
            )}
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
};

export default Login;
